import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Modal,
  Input,
  DatePicker,
  Select,
  Tabs,
  Card,
  Pagination,
} from 'antd';

import InfoView from 'components/common/InfoView';
import {
  FundOutlined,
} from '@ant-design/icons';
import {
  fetchSettlementReports,
  deleteSettlementReport,
} from '../../redux/actions/Data';
import SettlementReport from '../Merchants/SettlementReport';
import { settlementReportsColumns } from '../../components/common/Columns';
import SettlementMetrics from './SettlementMetrics';
import Can from '../../util/can';
import { SETTLEMENT_REPORTS } from '../../constants/resources';

const { RangePicker } = DatePicker;

const SettlementReports = () => {
  const dispatch = useDispatch();
  const {
    data: settlementReports,
    loading,
    pageSize,
    currentPage,
    totalRecords,
  } = useSelector(({ data }) => data.settlementReports);

  const [visibleSettlementReportModal, setVisibleSettlementReportModal] = useState(false);
  const [settlementReportWallet, setSettlementReportWallet] = useState({});
  const [editable, setEditable] = useState({});
  const [dateRange, setDateRange] = useState([]);
  const [searchValue, setSearchValue] = useState(null);
  const [searchParams, setSearchParams] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('PENDING');

  const getFilters = () => {
    let filters = {};

    if (dateRange && dateRange.length > 0) {
      if (dateRange[0].length > 2) {
        filters = {
          ...filters,
          begin_date: {
            $gte: `${dateRange[0]}T00:00:00.000Z`,
            $lte: `${dateRange[1]}T23:59:59.999Z`,
          },
        };
      }
    }

    if (selectedStatus && selectedStatus !== 'ALL') {
      filters = {
        ...filters,
        status: selectedStatus,
      };
    }

    if (searchValue && Array.isArray(searchParams)) {
      filters = {
        ...filters,
        $or: searchParams,
      };
    }

    if (selectedCurrency && selectedCurrency !== 'CUR') {
      filters = {
        ...filters,
        wallet_currency_code: selectedCurrency,
      };
    }

    return filters;
  };

  useEffect(() => {
    let pagination = `page=${currentPage}&limit=${pageSize}&sort={"statement_date": -1 }`;
    pagination += `&filter=${JSON.stringify(getFilters())}`;
    dispatch(fetchSettlementReports(pagination));
  }, [dispatch]);

  const handleDeleteRecord = (id) => {
    dispatch(deleteSettlementReport(id));
  };

  const handleChange = (value, key) => {
    let _dateRange = dateRange;
    let _currency = selectedCurrency;
    let _status = selectedStatus;
    if (key === 'date_range') {
      if (value[0].length > 2) {
        _dateRange = value;
        setDateRange(value);
      } else {
        setDateRange([]);
        _dateRange = [];
      }
    } else if (key === 'currency') {
      setSelectedCurrency(value || 'CUR');
      _currency = value;
    } else if (key === 'status') {
      setSelectedStatus(value || 'PENDING');
      _status = value;
    }

    let filters = {
      ...getFilters(),
    };

    if (_dateRange && _dateRange?.length) {
      filters = {
        ...filters,
        begin_date: {
          $gte: `${_dateRange[0]}T00:00:00.000Z`,
          $lte: `${_dateRange[1]}T23:59:59.999Z`,
        },
      };
    } else {
      delete filters?.begin_date;
    }

    if (_currency && _currency !== 'CUR') {
      filters = {
        ...filters,
        wallet_currency_code: _currency,
      };
    } else {
      delete filters?.wallet_currency_code;
    }

    if (_status && _status !== 'ALL') {
      filters = {
        ...filters,
        status: _status,
      };
    } else {
      delete filters?.status;
    }

    let params = `limit=${pageSize}&sort={"statement_date": -1 }`;
    params += `&filter=${JSON.stringify(filters)}`;
    dispatch(fetchSettlementReports(params));
  };

  const handleOnChange = (pagination, _filters, sort) => {
    if (pagination && sort) {
      const paginate = `page=${pagination.current}&limit=${pageSize}`;
      let sortParams = 'sort={"statement_date": -1 }';
      if (Object.keys(sort).length && sort.field) {
        sortParams = `sort={"${sort.field}": ${sort.order === 'ascend' ? '1' : '-1'} }`;
      }
      let paginateAndSort = `${paginate}&${sortParams}`;
      paginateAndSort += `&filter=${JSON.stringify(getFilters())}`;
      dispatch(fetchSettlementReports(paginateAndSort));
    }
  };

  const handleSearch = (searchV, options = '') => {
    const _searchValue = searchV.trim().replace(/([^a-zA-Z0-9])/g, '\\$1');
    setSearchValue(_searchValue);
    const searchFields = settlementReportsColumns().filter(
      (item) => item.search !== false,
    ).map((row) => ({ index: row.dataIndex, type: (row && row.type) || 'string' }));
    searchFields.push({ index: 'business_name_parent1', type: 'string' });
    searchFields.push({ index: 'business_name_parent2', type: 'string' });
    const _searchParams = searchFields.map((field) => ({
      [field.index]: field.type === 'number'
        ? Number(_searchValue.trim())
        : { $regex: _searchValue.trim(), $options: 'i' },
    })).filter((item) => !Number.isNaN(item[Object.keys(item)[0]]));

    setSearchParams(_searchParams);

    const filters = {
      ...getFilters(),
      $or: _searchParams,
    };

    const search = `filter=${JSON.stringify(filters)}`;
    dispatch(fetchSettlementReports(search, options));

    return _searchParams;
  };

  const handleEditRecord = (record) => {
    setEditable(record);
    setSettlementReportWallet({
      code: record.wallet_currency_code,
    });
    setVisibleSettlementReportModal(true);
  };

  const closeSettlementReport = () => {
    setVisibleSettlementReportModal(false);
    handleSearch(searchValue || '');
  };

  const tableProps = {
    loading,
    pagination: {
      current: currentPage,
      pageSize,
      total: totalRecords,
      showSizeChanger: false,
    },
    rowKey: '_id',
    onChange: handleOnChange,
    columns: settlementReportsColumns(
      handleDeleteRecord,
      handleEditRecord,
    ),
    dataSource: settlementReports,
  };

  const currencyStats = { USD: {}, EUR: {} };
  for (let i = 0; i < settlementReports.length; i += 1) {
    const el = settlementReports[i];
    const prevCurrObj = currencyStats[el.wallet_currency_code] || {};
    const newCurrObj = {};

    newCurrObj.processing_profit = Number(prevCurrObj.processing_profit || 0)
      + Number(el.processing_profit || 0);
    newCurrObj.overall_usd_profit = Number(prevCurrObj.overall_usd_profit || 0)
      + Number(el.overall_usd_profit || 0);
    newCurrObj.overall_usdt_profit = Number(prevCurrObj.overall_usdt_profit || 0)
      + Number(el.overall_usdt_profit || 0);

    newCurrObj.rolling_reserve_amount = Number(prevCurrObj.rolling_reserve_amount || 0)
      + Number(el.rolling_reserve_amount || 0);

    if (el.rrstatus === 'COMPLETED') {
      newCurrObj.rolling_reserve_settled = Number(prevCurrObj.rolling_reserve_settled || 0)
        + Number(el.rolling_reserve_amount || 0);
    }

    newCurrObj.successful_amount = Number(prevCurrObj.successful_amount || 0)
      + Number(el.successful_amount || 0);
    newCurrObj.successful_count = Number(prevCurrObj.successful_count || 0)
      + Number(el.successful_count || 0);
    newCurrObj.failed_amount = Number(prevCurrObj.failed_amount || 0)
      + Number(el.failed_amount || 0);
    newCurrObj.failed_count = Number(prevCurrObj.failed_count || 0)
      + Number(el.failed_count || 0);

    newCurrObj.refunds_amount = Number(prevCurrObj.refunds_amount || 0)
      + Number(el.refunds_amount || 0);
    newCurrObj.refunds_count = Number(prevCurrObj.refunds_count || 0)
      + Number(el.refunds_count || 0);

    newCurrObj.chargebacks_amount = Number(prevCurrObj.chargebacks_amount || 0)
      + Number(el.chargebacks_amount || 0);
    newCurrObj.chargebacks_count = Number(prevCurrObj.chargebacks_count || 0)
      + Number(el.chargebacks_count || 0);

    currencyStats[el.wallet_currency_code] = newCurrObj;
  }

  return (
    (
      <div>
        <div className="gx-d-flex justify-content-center">
          <Input.Search
            data-testid="search-input"
            placeholder="Search reports"
            enterButton
            allowClear
            onSearch={(value) => handleSearch(value)}
          />
          <div className="gx-ml-2 gx-mr-2">
            <Select
              style={{ width: '100%' }}
              value={`${selectedStatus || 'STATUS'}`}
              onChange={(e) => handleChange(e, 'status')}
            >
              <Select.Option value="ALL">ALL</Select.Option>
              <Select.Option value="COMPLETED">Settled</Select.Option>
              <Select.Option value="PENDING">Un-Settled</Select.Option>
            </Select>
          </div>
          <div className="gx-mr-2">
            <Select
              style={{ width: '100%' }}
              value={`${selectedCurrency || 'CUR'}`}
              onChange={(e) => handleChange(e, 'currency')}
            >
              <Select.Option value="CUR">CUR</Select.Option>
              <Select.Option value="USD">USD</Select.Option>
              <Select.Option value="EUR">EUR</Select.Option>
            </Select>
          </div>
          <div>
            <RangePicker onChange={(_, dateString) => handleChange(dateString, 'date_range')} style={{ width: '100%' }} />
          </div>
        </div>
        <div className="gx-d-flex justify-content-center">
          <Table size="small" data-testid="settlement-reports-table" className="gx-table-responsive" {...tableProps} />
        </div>
        <Modal
          open={visibleSettlementReportModal}
          onCancel={closeSettlementReport}
          width="90%"
          footer={null}
          centered
          destroyOnClose
        >
          <SettlementReport {...{
            wallet: settlementReportWallet,
            handleChange: () => { },
            isAffiliate: editable.is_affiliate,
            editable,
            // selectedMerchant: {
            //   _id: editable?.merchant_id,
            //   merchant_id: editable?.merchant_id,
            //   merchant_code: editable?.merchant_code,
            //   business_name: editable?.business_name,
            //   card_settlement_period: editable?.card_settlement_period,
            //   enable_affiliate: editable?.is_affiliate,
            // },
          }}
          />
        </Modal>
        <InfoView />
      </div>
    )
  );
};

export default SettlementReports;
